import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import { OfferPageRoomOption, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pane from 'components/Common/Pane'
import AlwaysOnRoomOptionMobile from '../AlwaysOnRoomOptionMobile'
import config from 'constants/config'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import SinglePropertyRoomSelectionModal from './SinglePropertyRoomSelectionModal'
import AccommodationMobileHeaderFilters from 'components/OfferPage/Accommodation/AccommodationMobileHeaderFilters'
import BodyText from 'components/Luxkit/Typography/BodyText'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  packageOptions: Array<OfferPackageOptionView>;
  roomOption?: OfferPageRoomOption;
  onBack: () => void;
  onClose: () => void;
}

function AlwaysOnRoomSelectionModal(props: Props) {
  const {
    offer,
    packageOptions,
    roomOption,
    onBack,
    onClose,
  } = props

  const [propertyMapping] = usePropertyOfferMapping(offer.property?.id, {
    disabled: !config.ACCOMM_USE_LPC_PROPERTY_PAGE,
  })

  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions)
  }, [packageOptions])

  if (propertyMapping) {
    return <SinglePropertyRoomSelectionModal
      onBack={onBack}
      onClose={onClose}
    />
  }

  return (
    <>
      <ModalHeader
        title="Select a room"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AccommodationMobileHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <>
          {packageOptionsByRoom.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="neutral-three">
                {pluralizeToString('room type', packageOptionsByRoom.length)} available
              </BodyText>
            </OptionsContainer>
          )}
          <Pane background="neutral-six">
            <VerticalSpacer gap={8}>
              {packageOptionsByRoom.map((item) => {
                if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
                  return null
                }
                return (<AlwaysOnRoomOptionMobile
                  key={item.roomId}
                  offer={offer}
                  packageViews={item.packageViews}
                  detailsModalMode="room-only"
                />)
              })}
            </VerticalSpacer>
          </Pane>
        </>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  offer: state.offer,
  rooms: state.rooms,
  roomOption: state.roomOption,
  packageOptions: getDurationPackageOptions(state, appState),
}), AlwaysOnRoomSelectionModal)
